import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { GetCountries } from "../../../api/countries";
import { GetPlansParams } from "../../../api/interfaces/subscriptions.interface";
import { GetPlans } from "../../../api/subscriptions";
import { useFetcher } from "../../../api/utils";
import { Plan } from "../../../assets/interfaces";
import { getStyles } from "../../../assets/js/utils/functions";
import TabToggle from "../../ui/tab-toggle";
import { FrontPagesNavigation } from "../commons";
import PricingCountrySelector from "./country-selector";
import PlanCard from "./plan-card";

const PricingHeader = () => {
  const [country, setCountry] = useState("NG");
  const { response: cResponse } = useFetcher(GetCountries, {});
  const { isLoading, error, response } = useFetcher<GetPlansParams>(GetPlans, { country });
  const thePlans = response?.data.plans ?? [];
  const plansWithoutKitchen = thePlans.filter((p) => p.type !== "KITCHEN" && p.type !== "STARTER");

  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("monthly");

  const tabOptions = [
    { id: "monthly", label: "Monthly" },
    { id: "quarterly", label: "Quarterly" },
    { id: "bi-annually", label: "Bi-Annually" },
    { id: "yearly", label: "Yearly" },
  ];

  useEffect(() => {
    if (response) {
      setPlans(
        plansWithoutKitchen.filter((plan: Plan) => {
          return {
            monthly: isMonthly(plan),
            quarterly: isQuarterly(plan),
            "bi-annually": isBiannually(plan),
            yearly: isAnnual(plan),
          }[interval];
        })
      );
    }
  }, [plansWithoutKitchen, interval]);

  const isMonthly = (plan) => plan.interval === 30 || plan.interval === 365;
  const isQuarterly = (plan) => plan.interval === 90 || plan.interval === 365;
  const isBiannually = (plan) => plan.interval === 180 || plan.interval === 365;
  const isAnnual = (plan) => plan.interval === 360;

  return (
    <header className="front-header section-spacing">
      <FrontPagesNavigation />
      <div className="body-padding">
        <div className="mt-8 sm:11.25 md:mt-15 flex flex-col items-center container">
          <div className="flex flex-col items-center">
            <div className="anim-word-up relative z-50">
              <PricingCountrySelector setCountry={setCountry} country={country} countries={cResponse?.data ?? []} />
            </div>
            <h1 className={classNames("font-light text-center text-black mt-5 heading-one")}>
              <div className="words-line py-1.5">
                <span className="anim-word-up inline-block" style={getStyles({ "--delay": "0.15s" })}>
                  Our
                </span>{" "}
                <span className="anim-word-up inline-block" style={getStyles({ "--delay": "0.3s" })}>
                  Affordable,
                </span>{" "}
              </div>{" "}
              <div className="words-line font-bold text-primary-500 py-1.5">
                <span className="anim-word-up inline-block" style={getStyles({ "--delay": "0.45s" })}>
                  Fair
                </span>{" "}
                <span className="anim-word-up inline-block" style={getStyles({ "--delay": "0.6s" })}>
                  Pricing
                </span>{" "}
              </div>{" "}
            </h1>
            <div
              className="m-auto w-[fit-content] mt-6 relative slide-text-in"
              style={getStyles({ "--delay": "0.7s" })}
            >
              <TabToggle onChange={(val) => setInterval(val)} items={tabOptions} />
            </div>

            {(isLoading || error || !plans) && (
              <div className="w-screen body-padding anim-word-up" style={getStyles({ "--delay": "0.8s" })}>
                <div className="pt-[50vw] bg-grey-fields-100 animate-pulse rounded-15 sm:rounded-20 lg:rounded-30 mt-8 sm:mt-10 lg:mt-15 border border-grey-border border-opacity-60 container"></div>
              </div>
            )}

            {!isLoading && plans && plans?.length > 0 && (
              <div className="flex justify-center items-start lg:items-stretch gap-8 flex-wrap mt-7.5 sm:mt-12.5 lg:mt-14">
                {plans
                  .sort((a, b) => a.amount - b.amount)
                  .map((plan, index) => (
                    <div
                      className="anim-word-up"
                      style={getStyles({ "--delay": `${(index + 1) * 0.25}s`, "--from": "15%", "--duration": "1s" })}
                    >
                      <PlanCard plan={plan} key={index} />
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default PricingHeader;
